import { FormProvider } from "react-hook-form";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import {
  formatEventsCount,
  formatReservationsCount,
  useBulkDeleteEventsDialog,
} from "../hooks/useBulkDeleteEventsDialog";
import { EventPickerMultiSelectField } from "./EventPickerMultiSelectField";
import styles from "./BulkDeleteEventsDialog.module.scss";

interface IBulkDeleteEventsDialogProps {
  visible: boolean;
  onHide: () => void;
}

/** Up to this many selections show as chips; past it, as a count. */
const MAX_SELECTED_CHIPS = 3;

export const BulkDeleteEventsDialog = ({
  visible,
  onHide,
}: IBulkDeleteEventsDialogProps) => {
  const {
    form,
    eventOptions,
    isLoadingEvents,
    selectedCount,
    selectedReservationsCount,
    canDelete,
    isDeleting,
    handleDelete,
  } = useBulkDeleteEventsDialog({ visible, onHide });

  const eventsEmptyMessage = isLoadingEvents
    ? "Зареждане..."
    : "Няма предстоящи събития.";

  return (
    <AppDialog
      header="Изтриване на събития"
      visible={visible}
      onHide={onHide}
      className={styles.root}
      // Ticking dozens of classes is real work; a stray click on the backdrop must not
      // throw it away. The X is the only way out.
      dismissableMask={false}
      closeOnEscape={false}
    >
      <FormProvider {...form}>
        <form className={styles.root_form}>
          <EventPickerMultiSelectField
            name="eventIds"
            label="Събития"
            options={eventOptions}
            emptyMessage={eventsEmptyMessage}
            maxSelectedLabels={MAX_SELECTED_CHIPS}
            selectedItemsLabel="{0} избрани събития"
          />

          <RenderWhen condition={selectedCount > 0}>
            <p className={styles.root_summary}>
              {`Избрани: ${formatEventsCount(selectedCount)} с общо ${formatReservationsCount(
                selectedReservationsCount,
              )}.`}
            </p>
          </RenderWhen>

          <p className={styles.root_hint} data-testid="bulk-delete-hint">
            Показват се всички предстоящи класове и услуги, без блокираното
            време. Търсете по име или дата и маркирайте всички намерени наведнъж.
            Избраните събития се изтриват така, както при изтриване едно по едно:
            остават в календара като изтрити и всяко може да бъде възстановено
            (груповият клас се връща без резервациите си). Резервациите в тях се
            отменят, а посещенията от карти и депозитите, с които са платени, се
            връщат на клиентите. Платеното в брой, с банкова карта или през
            Stripe не се връща автоматично. Клиентите не получават известие.
          </p>

          <div className={styles.root_footer}>
            <DefaultButton
              label="Изтрий"
              onClick={handleDelete}
              disabled={!canDelete}
              loading={isDeleting}
            />
          </div>
        </form>
      </FormProvider>
    </AppDialog>
  );
};
